import { MRT_ColumnDef } from 'material-react-table';
import { FC, useMemo } from 'react';
import { useServiceProviderListApi, useServiceProviderMetadataApi, useUpsertServiceProvider } from 'src/use-cases/settings/useServiceProvider';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';

import { ServiceProviderOverViewDto, ServiceProviderRequestDto } from 'src/generated/services/TFinancialApi';
import { newFormatDateString } from 'src/utils/date';
import { useSettingsTable } from 'src/presentations/hooks/useSettingsTable';
import { useTableFilters, SERVICE_PROVIDER_FILTER_MAPPING } from 'src/presentations/hooks/useTableFilters';
import { SettingsTableLayout } from 'src/presentations/components/templates/SettingsTableLayout';
import { ServiceProviderDetail } from './ServiceProviderDetail';
import { SettingsColumnHeader } from '../SettingsColumnHeader';
interface ServiceProviderTableProps {
  searchQuery?: string;
}

export const ServiceProviderMain: FC<ServiceProviderTableProps> = (props) => {
  const { searchQuery } = props;

  // Use the reusable settings table hook
  const {
    selectedEntityId,
    setSelectedEntityId,
    filters,
    entities,
    entitiesTotal,
    isLoading,
    isError,
    pagination,
    sorting,
    handleSortingChange,
    onChangePage,
    onChangePageSize,
    handleEntitySave,
    getTextFieldProps,
    clearValidationErrors,
    updateFilter,
    handleTableSort,
    metadataData,
    refetch,
    refetchMetadata,
  } = useSettingsTable<ServiceProviderOverViewDto, ServiceProviderRequestDto>({
    entityName: 'Service provider',
    requiredFields: ['name', 'code'],
    useEntityList: useServiceProviderListApi,
    useEntityMetadata: useServiceProviderMetadataApi,
    useUpsertEntity: useUpsertServiceProvider,
    searchQuery,
    initialSorting: [{ id: 'id', desc: false }],
  });

  // Use the reusable table filters hook
  const { handleTableFilter } = useTableFilters<ServiceProviderRequestDto>({
    updateFilter,
    refetch,
    refetchMetadata,
  });

  // Table filter handler with service provider specific mapping
  const handleServiceProviderFilter = (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
    handleTableFilter(columnId, selectedValues, allOptions, SERVICE_PROVIDER_FILTER_MAPPING);
  };

  const columns = useMemo<MRT_ColumnDef<ServiceProviderOverViewDto>[]>(
    () => [
      {
        enableEditing: false,
        accessorKey: 'id',
        header: 'ID',
      },
      {
        accessorKey: 'name',
        header: 'Service Providers',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.names);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.names}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleServiceProviderFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: {
          ...getTextFieldProps('name'),
          placeholder: 'Enter name',
        },
      },
      {
        accessorKey: 'code',
        header: 'Code',
        size: 150,
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.codes);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.codes}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleServiceProviderFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: {
          ...getTextFieldProps('code'),
          placeholder: 'Enter code',
        },
      },
      {
        accessorKey: 'updatedOn',
        header: 'Last Updated',
        size: 150,
        enableSorting: false,
        enableEditing: false,
        Header: ({ column }) => <SettingsColumnHeader column={column} onSort={handleTableSort} />,
        accessorFn: (row: ServiceProviderOverViewDto) => <>{newFormatDateString(row?.updatedOn)}</>,
      },
      {
        accessorKey: 'parentServiceProviderId',
        header: 'Parent Service Provider',
        size: 180,
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.parentServiceProviderIds);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.parentServiceProviderIds?.map(String)}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleServiceProviderFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
      },
    ],
    [getTextFieldProps, metadataData?.data, handleTableSort, handleServiceProviderFilter, filters]
  );

  // Custom cell props to handle row clicks for detail panel
  const customCellProps = ({ row, column }) => ({
    onClick: () => {
      if (column?.id !== 'mrt-row-actions') {
        setSelectedEntityId(row.original.id);
      }
    },
    sx: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        textDecoration: 'underline',
      },
    },
  });

  return (
    <SettingsTableLayout
      title='Service Providers'
      subtitle='Manage the service providers list'
      backPath='/settings'
      columns={columns}
      data={entities}
      totalItems={entitiesTotal}
      isLoading={isLoading}
      isError={isError}
      pagination={pagination}
      sorting={sorting}
      onChangePage={onChangePage}
      onChangePageSize={onChangePageSize}
      onSortingChange={handleSortingChange}
      onSaveEntity={handleEntitySave}
      onCancelEdit={clearValidationErrors}
      selectedEntityId={selectedEntityId}
      onCloseDetail={() => setSelectedEntityId(null)}
      renderDetail={(entityId) => <ServiceProviderDetail serviceProviderId={entityId as number} onClose={() => setSelectedEntityId(null)} />}
      customCellProps={customCellProps}
    />
  );
};

export default ServiceProviderMain;
